import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  disableMfa,
  enrollBegin,
  enrollConfirm,
  regenerateRecoveryCodes,
} from '../api/mfa.api';

type DialogState =
  | { kind: 'closed' }
  | {
      kind: 'enrolling';
      step: 'qr' | 'codes';
      provisionalSecret: string;
      qrDataUrl: string;
      otpauthUri: string;
      code: string;
      submitting: boolean;
      error: string;
      recoveryCodes: string[];
    }
  | { kind: 'disabling'; password: string; submitting: boolean; error: string }
  | {
      kind: 'regenerating';
      step: 'confirm' | 'codes';
      submitting: boolean;
      error: string;
      recoveryCodes: string[];
    };

const errorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof AxiosError) {
    return String(err.response?.data?.message ?? fallback);
  }
  return fallback;
};

const RecoveryCodesList = ({ codes }: { codes: string[] }) => (
  <Box
    sx={{
      bgcolor: 'background.level1',
      p: 2,
      borderRadius: 'md',
      fontFamily: 'monospace',
    }}
  >
    <Stack spacing={0.5}>
      {codes.map((c) => (
        <Typography key={c} sx={{ fontFamily: 'monospace' }}>
          {c}
        </Typography>
      ))}
    </Stack>
  </Box>
);

interface SecuritySettingsProps {
  mfaEnabled: boolean;
}

export const SecuritySettings = ({ mfaEnabled }: SecuritySettingsProps) => {
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const refreshUser = () => queryClient.invalidateQueries('user');

  const startEnrolment = async () => {
    try {
      const { provisionalSecret, qrDataUrl, otpauthUri } = await enrollBegin();
      setDialog({
        kind: 'enrolling',
        step: 'qr',
        provisionalSecret,
        qrDataUrl,
        otpauthUri,
        code: '',
        submitting: false,
        error: '',
        recoveryCodes: [],
      });
    } catch (err) {
      // The enrol-begin call is preflight; surface the message inline rather
      // than opening a modal we'd immediately have to close.
      // eslint-disable-next-line no-alert
      window.alert(errorMessage(err, t('mfa.enrol_error_qr_failed')));
    }
  };

  const submitEnrolment = async () => {
    if (dialog.kind !== 'enrolling') return;
    if (!dialog.code.trim()) {
      setDialog({ ...dialog, error: t('mfa.enrol_error_empty') });
      return;
    }
    setDialog({ ...dialog, submitting: true, error: '' });
    try {
      const { recoveryCodes } = await enrollConfirm(
        dialog.provisionalSecret,
        dialog.code.trim()
      );
      setDialog({ ...dialog, step: 'codes', submitting: false, recoveryCodes });
      refreshUser();
    } catch (err) {
      setDialog({
        ...dialog,
        submitting: false,
        error: errorMessage(err, t('mfa.enrol_error_bad_code')),
      });
    }
  };

  const startDisable = () =>
    setDialog({ kind: 'disabling', password: '', submitting: false, error: '' });

  const submitDisable = async () => {
    if (dialog.kind !== 'disabling') return;
    if (!dialog.password.length) {
      setDialog({ ...dialog, error: t('mfa.disable_error_empty') });
      return;
    }
    setDialog({ ...dialog, submitting: true, error: '' });
    try {
      await disableMfa(dialog.password);
      setDialog({ kind: 'closed' });
      refreshUser();
    } catch (err) {
      setDialog({
        ...dialog,
        submitting: false,
        error: errorMessage(err, t('mfa.disable_error_failed')),
      });
    }
  };

  const startRegenerate = () =>
    setDialog({
      kind: 'regenerating',
      step: 'confirm',
      submitting: false,
      error: '',
      recoveryCodes: [],
    });

  const submitRegenerate = async () => {
    if (dialog.kind !== 'regenerating') return;
    setDialog({ ...dialog, submitting: true, error: '' });
    try {
      const { recoveryCodes } = await regenerateRecoveryCodes();
      setDialog({ ...dialog, step: 'codes', submitting: false, recoveryCodes });
    } catch (err) {
      setDialog({
        ...dialog,
        submitting: false,
        error: errorMessage(err, t('mfa.regenerate_error_failed')),
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography level="h4">{t('mfa.section_title')}</Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: 'background.paper',
          boxShadow: 'sm',
          borderRadius: 'md',
          p: 2,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography>{t('mfa.two_step')}</Typography>
          <Chip color={mfaEnabled ? 'success' : 'neutral'} size="sm">
            {mfaEnabled ? t('mfa.status_on') : t('mfa.status_off')}
          </Chip>
        </Stack>
        <Typography level="body-sm">{t('mfa.explainer')}</Typography>

        {mfaEnabled ? (
          <Stack gap={1}>
            <Button variant="soft" onClick={startRegenerate}>
              {t('mfa.regenerate_button')}
            </Button>
            <Button variant="soft" color="danger" onClick={startDisable}>
              {t('mfa.disable_button')}
            </Button>
          </Stack>
        ) : (
          <Button variant="solid" onClick={startEnrolment}>
            {t('mfa.enable_button')}
          </Button>
        )}
      </Box>

      {/* Enrolment dialog */}
      <Modal
        open={dialog.kind === 'enrolling'}
        onClose={() => setDialog({ kind: 'closed' })}
      >
        <ModalDialog sx={{ maxWidth: 480 }}>
          {dialog.kind === 'enrolling' && dialog.step === 'qr' && (
            <>
              <Typography level="h4">{t('mfa.enrol_title')}</Typography>
              <Typography level="body-sm">
                {t('mfa.enrol_instructions')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <img
                  src={dialog.qrDataUrl}
                  alt={t('mfa.enrol_qr_alt')}
                  width={200}
                  height={200}
                />
              </Box>
              <Typography level="body-xs" sx={{ wordBreak: 'break-all', mb: 2 }}>
                {t('mfa.enrol_manual_key')}{' '}
                <strong>{dialog.provisionalSecret}</strong>
              </Typography>
              <FormControl>
                <FormLabel>{t('mfa.enrol_code_label')}</FormLabel>
                <Input
                  value={dialog.code}
                  onChange={(e) =>
                    setDialog({ ...dialog, code: e.target.value, error: '' })
                  }
                  placeholder={t('mfa.enrol_code_placeholder')}
                />
              </FormControl>
              {dialog.error && (
                <Alert color="danger" sx={{ mt: 1 }}>
                  {dialog.error}
                </Alert>
              )}
              <Stack
                direction="row"
                gap={1}
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <Button
                  variant="plain"
                  onClick={() => setDialog({ kind: 'closed' })}
                >
                  {t('mfa.cancel')}
                </Button>
                <Button
                  variant="solid"
                  loading={dialog.submitting}
                  onClick={submitEnrolment}
                >
                  {t('mfa.confirm')}
                </Button>
              </Stack>
            </>
          )}
          {dialog.kind === 'enrolling' && dialog.step === 'codes' && (
            <>
              <Typography level="h4">{t('mfa.codes_title')}</Typography>
              <Typography level="body-sm" sx={{ mb: 2 }}>
                {t('mfa.codes_intro')}
              </Typography>
              <RecoveryCodesList codes={dialog.recoveryCodes} />
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="solid"
                  onClick={() => setDialog({ kind: 'closed' })}
                >
                  {t('mfa.done')}
                </Button>
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>

      {/* Disable dialog */}
      <Modal
        open={dialog.kind === 'disabling'}
        onClose={() => setDialog({ kind: 'closed' })}
      >
        <ModalDialog sx={{ maxWidth: 420 }}>
          {dialog.kind === 'disabling' && (
            <>
              <Typography level="h4">{t('mfa.disable_title')}</Typography>
              <Typography level="body-sm" sx={{ mb: 2 }}>
                {t('mfa.disable_intro')}
              </Typography>
              <FormControl>
                <FormLabel>{t('mfa.disable_password_label')}</FormLabel>
                <Input
                  type="password"
                  value={dialog.password}
                  onChange={(e) =>
                    setDialog({ ...dialog, password: e.target.value, error: '' })
                  }
                />
              </FormControl>
              {dialog.error && (
                <Alert color="danger" sx={{ mt: 1 }}>
                  {dialog.error}
                </Alert>
              )}
              <Stack
                direction="row"
                gap={1}
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <Button
                  variant="plain"
                  onClick={() => setDialog({ kind: 'closed' })}
                >
                  {t('mfa.cancel')}
                </Button>
                <Button
                  variant="solid"
                  color="danger"
                  loading={dialog.submitting}
                  onClick={submitDisable}
                >
                  {t('mfa.disable_submit')}
                </Button>
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>

      {/* Regenerate dialog */}
      <Modal
        open={dialog.kind === 'regenerating'}
        onClose={() => setDialog({ kind: 'closed' })}
      >
        <ModalDialog sx={{ maxWidth: 480 }}>
          {dialog.kind === 'regenerating' && dialog.step === 'confirm' && (
            <>
              <Typography level="h4">{t('mfa.regenerate_title')}</Typography>
              <Typography level="body-sm" sx={{ mb: 2 }}>
                {t('mfa.regenerate_intro')}
              </Typography>
              {dialog.error && <Alert color="danger">{dialog.error}</Alert>}
              <Stack
                direction="row"
                gap={1}
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <Button
                  variant="plain"
                  onClick={() => setDialog({ kind: 'closed' })}
                >
                  {t('mfa.cancel')}
                </Button>
                <Button
                  variant="solid"
                  loading={dialog.submitting}
                  onClick={submitRegenerate}
                >
                  {t('mfa.regenerate_submit')}
                </Button>
              </Stack>
            </>
          )}
          {dialog.kind === 'regenerating' && dialog.step === 'codes' && (
            <>
              <Typography level="h4">
                {t('mfa.regenerate_codes_title')}
              </Typography>
              <Typography level="body-sm" sx={{ mb: 2 }}>
                {t('mfa.regenerate_codes_intro')}
              </Typography>
              <RecoveryCodesList codes={dialog.recoveryCodes} />
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="solid"
                  onClick={() => setDialog({ kind: 'closed' })}
                >
                  {t('mfa.done')}
                </Button>
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default SecuritySettings;
